// When the Chaptery desktop app opens one of its read-only embeds (World Building, Storyboard,
// Writing Dashboard), it passes the signed-in user's session in the URL fragment:
//
//   <site>/world?readonly=true&bookId=...#chaptery_auth=<base64(JSON { username, idToken, accessToken, refreshToken })>
//
// Seed Amplify's session keys from it *before* Amplify initialises, so the embed renders
// authenticated on the first load instead of racing the auth check and bouncing to the login
// page. The fragment is stripped immediately so the tokens don't linger in the URL.
//
// Honoured only on those embed loads, and kept to the tab (#1034). Anyone can craft a link
// with their own tokens in it; written into the saved sign-in on any page, it signed the
// visitor into the sender's account for good (login CSRF). Now:
//   - the session lives in this tab's sessionStorage, which Amplify reads only for an embed
//     load (configureAuthStorage), so it never replaces the visitor's own sign-in;
//   - a load that is not an embed drops it;
//   - a page load that started on it shows only the read-only embeds: anywhere else, App
//     shows a notice instead of the site (embedSessionLoad).
// The desktop has opened exactly these three paths with ?readonly=true since 1.2.6. See
// "Embedded pages" in docs/desktop-client.md.
//
// Imported first in index.tsx, for its side effect (runs before Amplify.configure).
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { sessionStorage as amplifySessionStorage } from 'aws-amplify/utils';

const EMBED_ROUTES = ['/world', '/storyboard', '/writing-dashboard'];
const EMBED_SESSION_KEY = 'chaptery.desktopEmbedSession';
const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

/** A location that is one of the desktop app's read-only embeds: the exact path, readonly=true. */
export const isEmbedLocation = (pathname: string, search: string): boolean =>
  EMBED_ROUTES.includes(pathname) && new URLSearchParams(search).get('readonly') === 'true';

const isEmbedLoad = (): boolean => isEmbedLocation(window.location.pathname, window.location.search);

function dropEmbedSession(): void {
  const keys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith(COGNITO_PREFIX)) keys.push(k);
  }
  keys.forEach((k) => sessionStorage.removeItem(k));
  sessionStorage.removeItem(EMBED_SESSION_KEY);
}

(function seedDesktopAuthFromUrl(): void {
  try {
    const embed = isEmbedLoad();
    // Anywhere but an embed, the visitor's own sign-in applies: drop any embed session.
    if (!embed) dropEmbedSession();

    const hash = window.location.hash;
    if (!hash || hash.indexOf('chaptery_auth=') === -1) return;

    const raw = new URLSearchParams(hash.replace(/^#/, '')).get('chaptery_auth');
    // Strip the tokens from the URL first, whatever happens next, so they never linger in
    // the address bar or history.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (!raw || !embed) return;

    const data = JSON.parse(atob(raw)) as {
      username?: string;
      idToken?: string;
      accessToken?: string;
      refreshToken?: string;
    };
    if (!data.username || !data.idToken || !data.accessToken) return;

    const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID || '';
    if (!clientId) return;

    // A new fragment replaces the tab's previous embed session; nothing of it lingers.
    dropEmbedSession();
    const prefix = `${COGNITO_PREFIX}${clientId}`;
    const u = data.username;
    sessionStorage.setItem(`${prefix}.LastAuthUser`, u);
    sessionStorage.setItem(`${prefix}.${u}.idToken`, data.idToken);
    sessionStorage.setItem(`${prefix}.${u}.accessToken`, data.accessToken);
    if (data.refreshToken) {
      sessionStorage.setItem(`${prefix}.${u}.refreshToken`, data.refreshToken);
    }
    sessionStorage.setItem(`${prefix}.${u}.clockDrift`, '0');
    sessionStorage.setItem(EMBED_SESSION_KEY, '1');
  } catch (err) {
    // Never block app startup on a malformed fragment.
    // eslint-disable-next-line no-console
    console.error('[chaptery] desktop auth bootstrap failed', err);
  }
})();

/**
 * True when this page load runs on a desktop embed's tab-scoped session. Otherwise Amplify
 * keeps its default, the visitor's own saved sign-in.
 */
export function desktopEmbedSession(): boolean {
  try {
    return sessionStorage.getItem(EMBED_SESSION_KEY) === '1' && isEmbedLoad();
  } catch {
    return false;
  }
}

/** Whether this page load started on an embed session, fixed for the load: App keeps such a
 *  tab on the read-only embeds, whatever the router does afterwards. */
export const embedSessionLoad: boolean = desktopEmbedSession();

/** Called by index.tsx right after Amplify.configure, which resets the token storage. */
export function configureAuthStorage(): void {
  if (embedSessionLoad) cognitoUserPoolsTokenProvider.setKeyValueStorage(amplifySessionStorage);
}
